import { Plus, Trash2 } from "lucide-react"
import { useEffect, useState } from "react"

import { type Estudio, type Experiencia, NIVELES_ESTUDIO } from "@/componentes/personas/persona"
import { Boton } from "@/componentes/ui/Boton"

/**
 * Estudios y experiencia laboral, como listas que se editan enteras.
 *
 * ⚠ **Se guardan ENTERAS** (ver `persona.ts::Curriculo`): añadir, quitar o corregir
 * una fila cambia la lista, y al guardar la lista reemplaza la del servidor.
 *
 * ⚠ **Las fechas de un empleo son «AAAA» o «AAAA-MM»**, en un campo de texto y no en
 * uno de fecha: un currículo dice «2019 – 2021», y un selector de día obligaría a
 * inventar el que nadie escribió.
 */

const NUEVO_ESTUDIO: Estudio = {
  nivel: "UNIVERSITARIO",
  titulo: "",
  institucion: null,
  anio_inicio: null,
  anio_fin: null,
  en_curso: false,
}

const NUEVA_EXPERIENCIA: Experiencia = {
  empresa: "",
  cargo: null,
  desde: null,
  hasta: null,
  actual: false,
  funciones: null,
}

const texto = (v: string) => (v.trim() === "" ? null : v)
const anio = (v: string) => (/^\d{4}$/.test(v) ? Number(v) : null)

/**
 * Un año que se puede teclear a medias: «20» se ve, pero no viaja hasta tener cuatro
 * cifras. Controlado por dentro, y se resincroniza cuando el valor cambia desde fuera
 * —una lectura IA que reemplaza la lista—.
 */
function CampoAnio({
  etiqueta,
  valor,
  desactivado = false,
  onCambio,
}: {
  etiqueta: string
  valor: number | null
  desactivado?: boolean
  onCambio: (anio: number | null) => void
}) {
  const [texto, setTexto] = useState(valor?.toString() ?? "")
  // biome-ignore lint/correctness/useExhaustiveDependencies: sólo cuando cambia DESDE FUERA
  useEffect(() => {
    if (valor !== anio(texto)) setTexto(valor?.toString() ?? "")
  }, [valor])
  return (
    <input
      aria-label={etiqueta}
      placeholder={`${etiqueta} (AAAA)`}
      inputMode="numeric"
      maxLength={4}
      disabled={desactivado}
      aria-invalid={texto !== "" && anio(texto) === null}
      className="campo w-32 tabular-nums"
      value={texto}
      onChange={(ev) => {
        setTexto(ev.target.value)
        onCambio(anio(ev.target.value))
      }}
    />
  )
}

function cambiar<T>(lista: T[], i: number, cambio: Partial<T>): T[] {
  return lista.map((fila, j) => (j === i ? { ...fila, ...cambio } : fila))
}

function Quitar({ onClick, etiqueta }: { onClick: () => void; etiqueta: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={etiqueta}
      className="toque-cristal flex h-9 w-9 shrink-0 items-center justify-center rounded"
    >
      <Trash2 size={16} aria-hidden={true} />
    </button>
  )
}

export function CurriculoDePersona({
  estudios,
  experiencias,
  onEstudios,
  onExperiencias,
}: {
  estudios: Estudio[]
  experiencias: Experiencia[]
  onEstudios: (lista: Estudio[]) => void
  onExperiencias: (lista: Experiencia[]) => void
}) {
  return (
    <>
      <section>
        <h3 className="rotulo mb-3">Estudios</h3>
        <ul className="space-y-3">
          {estudios.map((e, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: filas sin id hasta guardarse
            <li key={i} className="superficie grid gap-2 p-3 sm:grid-cols-[10rem_1fr_auto]">
              <select
                aria-label="Nivel"
                className="campo"
                value={e.nivel}
                onChange={(ev) =>
                  onEstudios(cambiar(estudios, i, { nivel: ev.target.value as Estudio["nivel"] }))
                }
              >
                {NIVELES_ESTUDIO.map(([v, t]) => (
                  <option key={v} value={v}>
                    {t}
                  </option>
                ))}
              </select>
              <input
                aria-label="Título"
                placeholder="Título"
                className="campo"
                value={e.titulo}
                onChange={(ev) => onEstudios(cambiar(estudios, i, { titulo: ev.target.value }))}
              />
              <Quitar
                etiqueta="Quitar estudio"
                onClick={() => onEstudios(estudios.filter((_, j) => j !== i))}
              />
              <input
                aria-label="Institución"
                placeholder="Institución"
                className="campo sm:col-span-2"
                value={e.institucion ?? ""}
                onChange={(ev) =>
                  onEstudios(cambiar(estudios, i, { institucion: texto(ev.target.value) }))
                }
              />
              <div className="flex flex-wrap items-center gap-2 sm:col-span-3">
                <CampoAnio
                  etiqueta="Desde"
                  valor={e.anio_inicio}
                  onCambio={(v) => onEstudios(cambiar(estudios, i, { anio_inicio: v }))}
                />
                <CampoAnio
                  etiqueta="Hasta"
                  valor={e.anio_fin}
                  desactivado={e.en_curso}
                  onCambio={(v) => onEstudios(cambiar(estudios, i, { anio_fin: v }))}
                />
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={e.en_curso}
                    onChange={(ev) =>
                      onEstudios(
                        cambiar(estudios, i, {
                          en_curso: ev.target.checked,
                          anio_fin: ev.target.checked ? null : e.anio_fin,
                        }),
                      )
                    }
                  />
                  En curso
                </label>
              </div>
            </li>
          ))}
        </ul>
        <Boton
          tamano="sm"
          className="mt-2"
          onClick={() => onEstudios([...estudios, NUEVO_ESTUDIO])}
        >
          <Plus size={14} /> Añadir estudio
        </Boton>
      </section>

      <section>
        <h3 className="rotulo mb-3">Experiencia laboral</h3>
        <ul className="space-y-3">
          {experiencias.map((x, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: filas sin id hasta guardarse
            <li key={i} className="superficie grid gap-2 p-3 sm:grid-cols-[1fr_1fr_auto]">
              <input
                aria-label="Empresa"
                placeholder="Empresa"
                className="campo"
                value={x.empresa}
                onChange={(ev) =>
                  onExperiencias(cambiar(experiencias, i, { empresa: ev.target.value }))
                }
              />
              <input
                aria-label="Cargo"
                placeholder="Cargo"
                className="campo"
                value={x.cargo ?? ""}
                onChange={(ev) =>
                  onExperiencias(cambiar(experiencias, i, { cargo: texto(ev.target.value) }))
                }
              />
              <Quitar
                etiqueta="Quitar experiencia"
                onClick={() => onExperiencias(experiencias.filter((_, j) => j !== i))}
              />
              <div className="flex flex-wrap items-center gap-2 sm:col-span-3">
                <input
                  aria-label="Desde"
                  placeholder="Desde (AAAA-MM)"
                  maxLength={7}
                  className="campo w-36 tabular-nums"
                  value={x.desde ?? ""}
                  onChange={(ev) =>
                    onExperiencias(cambiar(experiencias, i, { desde: texto(ev.target.value) }))
                  }
                />
                <input
                  aria-label="Hasta"
                  placeholder="Hasta (AAAA-MM)"
                  maxLength={7}
                  disabled={x.actual}
                  className="campo w-36 tabular-nums"
                  value={x.hasta ?? ""}
                  onChange={(ev) =>
                    onExperiencias(cambiar(experiencias, i, { hasta: texto(ev.target.value) }))
                  }
                />
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={x.actual}
                    onChange={(ev) =>
                      onExperiencias(
                        cambiar(experiencias, i, {
                          actual: ev.target.checked,
                          hasta: ev.target.checked ? null : x.hasta,
                        }),
                      )
                    }
                  />
                  Empleo actual
                </label>
              </div>
              <textarea
                aria-label="Funciones"
                placeholder="Funciones y logros"
                rows={2}
                className="campo sm:col-span-3"
                value={x.funciones ?? ""}
                onChange={(ev) =>
                  onExperiencias(cambiar(experiencias, i, { funciones: texto(ev.target.value) }))
                }
              />
            </li>
          ))}
        </ul>
        <Boton
          tamano="sm"
          className="mt-2"
          onClick={() => onExperiencias([...experiencias, NUEVA_EXPERIENCIA])}
        >
          <Plus size={14} /> Añadir experiencia
        </Boton>
      </section>
    </>
  )
}
